import { nowMillis } from "@/lib/date-utils"
import { ResponseBuffer, StreamedResponse } from "@/lib/response-buffer"

// The slice of axios the fetcher uses, narrow enough for tests to fake.
export interface HttpClient {
  get(
    url: string,
    config: { validateStatus: (status: number) => boolean }
  ): Promise<{ status: number; data?: unknown }>
}

// Poll scheduling: the socket's ready signal is the announcement path, so
// the poll is a fallback for a failed socket. Attempts come at a flat ~15s
// spacing — long enough that no genuinely slow response beats its own
// announcement — jittered ±20% so a fleet of waits orphaned by one outage
// doesn't poll in lockstep. The tick is just the scheduler's granularity.
// Matches the iOS client.
const POLL_TICK_MS = 3_000
const POLL_DELAY_MS = 15_000

// A fast block can complete and broadcast its ready signal while a fresh page
// load's socket is still connecting; so probe right away when registered
// and then again after a short delay.
const REGISTER_RETRY_DELAY_MS = 2_000

function jitteredPollDelay(): number {
  return POLL_DELAY_MS * (0.8 + Math.random() * 0.4)
}

/**
 * Fetches completed slow responses over HTTP (GET /api/streamed-responses/
 * {id}) and feeds them into the ResponseBuffer, where waiters resolve exactly
 * as if the socket had delivered them.
 *
 * Two triggers: the socket's "slow-response-ready" signal (responseReady),
 * and a steady poll of ids still waiting. The poll is what makes delivery
 * hang-proof — it recovers responses whose ready signal was missed because
 * the socket was disconnected, reconnecting, or never up at all.
 *
 * The endpoint answers 200 with the {id, status, result} envelope once the
 * result is stashed, 202 while pending, and 404 when the stash has no entry.
 * The pending marker is stashed before the id is ever issued, so 404 means
 * expired or not this session's id: the response is not coming, and the wait
 * fails immediately. 202 and transient network failures mean "keep trying",
 * bounded by the wait deadline in the ResponseBuffer. Fetching is this client's only delivery path.
 */
export class StreamedResponseFetcher {
  // Ready signals that arrived before (or without) a local waiter — another
  // tab's responses, or a fast slow block beating its own envelope. Parked
  // (bounded) and claimed the moment the waiter registers, so a completed
  // response is never taxed a poll tick by arrival order.
  static MAX_UNCLAIMED_READY = 10

  private inFlight = new Set<string>()
  private unclaimedReady = new Set<string>()
  private pollDueAt = new Map<string, number>()
  private pollTimer: ReturnType<typeof setInterval> | undefined

  constructor(
    private axios: HttpClient,
    private responseBuffer: ResponseBuffer,
    private refreshTokenIfNeeded: () => Promise<void>
  ) {}

  start() {
    this.pollTimer ??= setInterval(() => this.poll(), POLL_TICK_MS)
  }

  stop() {
    clearInterval(this.pollTimer)
    this.pollTimer = undefined
  }

  // The server says this response is fetchable. The signal goes to the
  // whole session room, so fetch only when this tab has a waiter;
  // otherwise park the id for waiterRegistered to claim.
  responseReady(streamingResponseId: string) {
    if (this.responseBuffer.isWaitingFor(streamingResponseId)) {
      void this.fetch(streamingResponseId)
      return
    }
    this.unclaimedReady.add(streamingResponseId)
    while (
      this.unclaimedReady.size > StreamedResponseFetcher.MAX_UNCLAIMED_READY
    ) {
      const [oldest] = this.unclaimedReady
      this.unclaimedReady.delete(oldest)
    }
  }

  // A waiter just registered; if its ready signal already arrived, fetch
  // immediately instead of waiting for the next poll.
  waiterRegistered(streamingResponseId: string) {
    if (this.unclaimedReady.delete(streamingResponseId)) {
      void this.fetch(streamingResponseId)
      return
    }
    // no parked signal; check if the broadcast may have been lost to a socket
    // that wasn't connected yet, so probe now and again shortly
    void this.fetch(streamingResponseId)
    this.pollDueAt.set(
      streamingResponseId,
      nowMillis() + REGISTER_RETRY_DELAY_MS
    )
  }

  private poll() {
    const waiting = new Set(this.responseBuffer.waitingResponseIds())
    for (const id of this.pollDueAt.keys()) {
      if (!waiting.has(id)) this.pollDueAt.delete(id)
    }

    const now = nowMillis()
    for (const id of waiting) {
      const dueAt = this.pollDueAt.get(id)
      if (dueAt === undefined) {
        this.pollDueAt.set(id, now + jitteredPollDelay())
      } else if (now >= dueAt) {
        this.pollDueAt.set(id, now + jitteredPollDelay())
        void this.fetch(id)
      }
    }
  }

  private async fetch(streamingResponseId: string) {
    if (this.inFlight.has(streamingResponseId)) return
    this.inFlight.add(streamingResponseId)
    try {
      await this.refreshTokenIfNeeded()
      const response = await this.axios.get(
        `/api/streamed-responses/${streamingResponseId}`,
        { validateStatus: (status) => [200, 202, 404].includes(status) }
      )
      if (response.status === 404) {
        this.responseBuffer.failWait(
          streamingResponseId,
          new Error(
            `streamed response ${streamingResponseId} not found: expired or never issued`
          )
        )
      } else if (
        response.status === 200 &&
        isCompletedEnvelope(response.data)
      ) {
        // Drops the payload if the waiter aborted or timed out mid-flight.
        this.responseBuffer.deliverIfWaiting(response.data)
      }
    } catch {
      // Network hiccup — the next poll retries, and the buffer's deadline
      // bounds the overall wait.
    } finally {
      this.inFlight.delete(streamingResponseId)
    }
  }
}

function isCompletedEnvelope(data: unknown): data is StreamedResponse<unknown> {
  return (
    !!data &&
    typeof data === "object" &&
    "status" in data &&
    (data.status === "success" || data.status === "error")
  )
}
